import { useCallback, useEffect, useRef, useState } from "react";
import { AppState, AppStateStatus } from "react-native";
import {
  getApprovedRefundProcesses,
  markAsNotified,
} from "@/services/refundProcessService";
import { RefundWithItem } from "@/types/refundWithItem";
import { getGlobalSessionId } from "@/services";

// Random interval between 10-20 seconds to prevent DDoS
const getRandomInterval = () => {
  return Math.floor(Math.random() * (20000 - 10000 + 1)) + 10000;
};

type UseRefundSuccessPollerProps = {
  onRefundSuccess: (_refundWithItem: RefundWithItem) => void;
  isModalVisible?: boolean;
};

export const useRefundSuccessPoller = ({
  onRefundSuccess,
  isModalVisible = false,
}: UseRefundSuccessPollerProps) => {
  const [isPolling, setIsPolling] = useState(false);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const appStateRef = useRef<AppStateStatus>(AppState.currentState);

  const pollForRefundSuccess = useCallback(async () => {
    const sessionId = getGlobalSessionId();

    if (!sessionId || appStateRef.current !== "active" || isModalVisible) {
      return;
    }

    try {
      console.log("Polling for approved refunds...");
      const approvedRefunds = await getApprovedRefundProcesses();

      if (approvedRefunds.length > 0) {
        const firstRefund = approvedRefunds[0];
        console.log("Found approved refund:", firstRefund);

        // Show success popup
        onRefundSuccess(firstRefund);

        // Mark as notified
        await markAsNotified({
          pk: firstRefund.refundProcess.pk,
          sk: firstRefund.refundProcess.sk,
        });

        console.log("Marked refund as notified");
      }
    } catch (error) {
      console.error("Error polling for refund success:", error);
    }
  }, [isModalVisible, onRefundSuccess]);

  const startPolling = useCallback(() => {
    const sessionId = getGlobalSessionId();
    if (timeoutRef.current || !sessionId) {
      console.log("🚫 Polling not started - already running or no sessionId");
      return;
    }

    console.log("✅ Starting polling...");
    setIsPolling(true);

    const scheduleNextPoll = () => {
      const interval = getRandomInterval();

      timeoutRef.current = setTimeout(async () => {
        await pollForRefundSuccess();
        if (
          timeoutRef.current &&
          appStateRef.current === "active" &&
          !isModalVisible
        ) {
          scheduleNextPoll();
        }
      }, interval);
    };

    scheduleNextPoll();
  }, [isModalVisible, pollForRefundSuccess]);

  const stopPolling = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    setIsPolling(false);
  }, []);

  useEffect(() => {
    const handleAppStateChange = (nextAppState: AppStateStatus) => {
      appStateRef.current = nextAppState;

      if (nextAppState === "active" && !isModalVisible) {
        console.log("App became active, starting refund polling");
        startPolling();
      } else {
        console.log("App went to background, stopping refund polling");
        stopPolling();
      }
    };

    const subscription = AppState.addEventListener(
      "change",
      handleAppStateChange
    );

    // Start polling if app is already active and modal is not visible
    if (AppState.currentState === "active" && !isModalVisible) {
      console.log(
        "🚀 Initial polling start - App is active and modal is not visible"
      );
      startPolling();
    }

    return () => {
      subscription.remove();
      stopPolling();
    };
  }, [isModalVisible]);

  // Separate useEffect to periodically check for session ID and start polling
  useEffect(() => {
    const checkForSessionId = () => {
      const sessionId = getGlobalSessionId();

      if (
        sessionId &&
        AppState.currentState === "active" &&
        !isModalVisible &&
        !timeoutRef.current
      ) {
        console.log("🚀 Starting polling after session ID became available");
        startPolling();
      }
    };

    // Check immediately
    checkForSessionId();

    // Check every 2 seconds until session ID is available
    const interval = setInterval(checkForSessionId, 2000);

    return () => {
      clearInterval(interval);
    };
  }, [isModalVisible]);

  return {
    isPolling,
    startPolling,
    stopPolling,
  };
};
